import { useState, useRef, useEffect, useCallback } from 'react';
import { Globe, Check } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { SUPPORTED_LANGUAGES } from '../config/languages';

function LanguageSelector() {
  const { language, setLanguage, supportedLanguages } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const optionRefs = useRef([]);

  // Hide language selector if only one language is supported
  if (supportedLanguages.length <= 1) {
    return null;
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setActiveIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Focus the active option when it changes
  useEffect(() => {
    if (isOpen && activeIndex >= 0 && optionRefs.current[activeIndex]) {
      optionRefs.current[activeIndex].focus();
    }
  }, [isOpen, activeIndex]);

  const handleLanguageChange = useCallback((langCode) => {
    setLanguage(langCode);
    setIsOpen(false);
    setActiveIndex(-1);
    // Return focus to the trigger button
    buttonRef.current?.focus();
  }, [setLanguage]);

  const handleButtonKeyDown = (e) => {
    switch (e.key) {
      case 'Enter':
      case ' ':
      case 'ArrowDown':
        e.preventDefault();
        setIsOpen(true);
        // Set active index to current language
        const currentIdx = supportedLanguages.indexOf(language);
        setActiveIndex(currentIdx >= 0 ? currentIdx : 0);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setIsOpen(true);
        setActiveIndex(supportedLanguages.length - 1);
        break;
      case 'Escape':
        if (isOpen) {
          e.preventDefault();
          setIsOpen(false);
          setActiveIndex(-1);
        }
        break;
    }
  };

  const handleOptionKeyDown = (e, index, langCode) => {
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        handleLanguageChange(langCode);
        break;
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex(index < supportedLanguages.length - 1 ? index + 1 : 0);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex(index > 0 ? index - 1 : supportedLanguages.length - 1);
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setActiveIndex(-1);
        buttonRef.current?.focus();
        break;
      case 'Tab':
        // Close dropdown on Tab and let focus move naturally
        setIsOpen(false);
        setActiveIndex(-1);
        break;
      case 'Home':
        e.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        e.preventDefault();
        setActiveIndex(supportedLanguages.length - 1);
        break;
    }
  };

  const currentLanguage = SUPPORTED_LANGUAGES[language];

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        ref={buttonRef}
        onClick={() => {
          const opening = !isOpen;
          setIsOpen(opening);
          if (opening) {
            const currentIdx = supportedLanguages.indexOf(language);
            setActiveIndex(currentIdx >= 0 ? currentIdx : 0);
          } else {
            setActiveIndex(-1);
          }
        }}
        onKeyDown={handleButtonKeyDown}
        className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
        aria-label="Select language"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <Globe className="w-4 h-4 text-gray-600" aria-hidden="true" />
        <span className="text-sm font-medium text-gray-700">
          {currentLanguage.flag} {currentLanguage.code.toUpperCase()}
        </span>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <ul
          role="listbox"
          aria-label="Select language"
          aria-activedescendant={activeIndex >= 0 ? `lang-option-${supportedLanguages[activeIndex]}` : undefined}
          className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 animate-fade-in"
        >
          {supportedLanguages.map((langCode, index) => {
            const lang = SUPPORTED_LANGUAGES[langCode];
            const isActive = langCode === language;

            return (
              <li
                key={langCode}
                id={`lang-option-${langCode}`}
                ref={(el) => (optionRefs.current[index] = el)}
                role="option"
                aria-selected={isActive}
                tabIndex={index === activeIndex ? 0 : -1}
                onClick={() => handleLanguageChange(langCode)}
                onKeyDown={(e) => handleOptionKeyDown(e, index, langCode)}
                className={`w-full flex items-center justify-between px-4 py-2 text-sm hover:bg-gray-50 transition-colors cursor-pointer ${
                  isActive ? 'bg-primary-50' : ''
                } ${index === activeIndex ? 'ring-2 ring-inset ring-primary-500' : ''}`}
              >
                <span className="flex items-center space-x-2">
                  <span aria-hidden="true">{lang.flag}</span>
                  <span className={isActive ? 'font-medium text-primary-600' : 'text-gray-700'}>
                    {lang.name}
                  </span>
                </span>
                {isActive && <Check className="w-4 h-4 text-primary-600" aria-hidden="true" />}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default LanguageSelector;
